import { createInterface } from "node:readline/promises";
import { stdin as input, stdout as output } from "node:process";

export async function promptLine(message: string): Promise<string> {
  const rl = createInterface({ input, output });
  try {
    const value = (await rl.question(message)).trim();
    if (!value) {
      throw new Error("Input is required.");
    }
    return value;
  } finally {
    rl.close();
  }
}

export async function promptPassword(message = "Password: "): Promise<string> {
  return new Promise((resolve, reject) => {
    const stdin = process.stdin;
    const wasRaw = stdin.isRaw;
    const wasPaused = stdin.isPaused();

    if (!stdin.isTTY) {
      reject(new Error("Password prompt requires an interactive terminal."));
      return;
    }

    output.write(message);

    stdin.setRawMode(true);
    stdin.resume();
    stdin.setEncoding("utf8");

    let password = "";

    const cleanup = (): void => {
      stdin.removeListener("data", onData);
      stdin.setRawMode(wasRaw ?? false);
      if (wasPaused) stdin.pause();
      else stdin.resume();
    };

    const onData = (char: string): void => {
      switch (char) {
        case "\n":
        case "\r":
        case "\u0004":
          cleanup();
          output.write("\n");
          if (!password) {
            reject(new Error("Password is required."));
            return;
          }
          resolve(password);
          break;
        case "\u0003":
          cleanup();
          output.write("\n");
          reject(new Error("Login cancelled."));
          break;
        case "\u007f":
        case "\b":
          if (password.length > 0) {
            password = password.slice(0, -1);
            output.write("\b \b");
          }
          break;
        default:
          if (char < " " && char !== "\t") return;
          password += char;
          output.write("*");
          break;
      }
    };

    stdin.on("data", onData);
  });
}

export async function promptCredentials(options: {
  account?: string;
  password?: string;
}): Promise<{ account: string; password: string }> {
  const account = options.account ?? (await promptLine("Email: "));
  const password = options.password ?? (await promptPassword());
  return { account, password };
}

export async function promptLoginMethod(): Promise<"browser" | "password"> {
  printLoginMethodMenu();
  const rl = createInterface({ input, output });
  try {
    const answer = (await rl.question("Enter choice [1]: ")).trim();
    if (!answer || answer === "1") return "browser";
    if (answer === "2") return "password";
    throw new Error("Invalid choice. Enter 1 or 2.");
  } finally {
    rl.close();
  }
}

function printLoginMethodMenu(): void {
  console.log("How would you like to log in?");
  console.log("  1. Browser authorization (recommended)");
  console.log("  2. Email and password");
}
